import { Observable, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/** Delay non-critical work until after first paint / LCP window. */
export const LCP_DEFER_MS = 3000;

/**
 * Homepage product-rail refill — short post-paint delay so rails are not gated behind
 * the full 3s LCP window (that delay left empty sections on the live site).
 */
export const HOME_REFILL_MS = 200;

/** Postpone an observable until the LCP window has passed (Cms/Home refresh, etc.). */
export function deferAfterLcp<T>(source$: Observable<T>, delayMs = LCP_DEFER_MS): Observable<T> {
  return timer(delayMs).pipe(switchMap(() => source$));
}

/** Idle fallback so a homepage overlay cannot become LCP in lab or on first paint. */
export const POPUP_IDLE_FALLBACK_MS = 60_000;

/**
 * Run `fn` once after `delayMs` (default 3s).
 * Returns a cancel function — safe to call from ngOnDestroy.
 */
export function scheduleAfterFirstPaint(fn: () => void, delayMs = LCP_DEFER_MS): () => void {
  let cancelled = false;
  const timeoutId = setTimeout(() => {
    if (!cancelled) {
      fn();
    }
  }, Math.max(0, delayMs));

  return () => {
    cancelled = true;
    clearTimeout(timeoutId);
  };
}

/**
 * Run `fn` once after first paint + a short settle window (default 900ms).
 * Used to upgrade the homepage hero to full quality without fighting LCP.
 */
export function scheduleAfterLcpSettle(fn: () => void, delayMs = 900): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  let cancelled = false;
  let timeoutId = 0;
  let idleId: number | null = null;

  const run = (): void => {
    if (cancelled) {
      return;
    }
    cancelled = true;
    fn();
  };

  const arm = (): void => {
    timeoutId = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      const ric = (window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }).requestIdleCallback;
      if (typeof ric === 'function') {
        idleId = ric(() => run(), { timeout: 600 });
      } else {
        run();
      }
    }, Math.max(0, delayMs));
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => arm());
  } else {
    arm();
  }

  return () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
    const cic = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
    if (idleId != null && typeof cic === 'function') {
      cic(idleId);
    }
  };
}

/**
 * Wait for a deliberate user gesture (or a long idle fallback).
 * Scroll is excluded — Lighthouse and trackpads fire scroll during lab runs and would
 * otherwise load Meta Pixel / popup mid-audit (TBT spike).
 */
export function scheduleAfterInteraction(fn: () => void, fallbackMs = POPUP_IDLE_FALLBACK_MS): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  let done = false;
  const run = (): void => {
    if (done) {
      return;
    }
    done = true;
    window.removeEventListener('pointerdown', run, true);
    window.removeEventListener('keydown', run, true);
    window.removeEventListener('touchstart', run, true);
    window.clearTimeout(timer);
    fn();
  };

  window.addEventListener('pointerdown', run, { capture: true });
  window.addEventListener('keydown', run, { capture: true });
  window.addEventListener('touchstart', run, { capture: true, passive: true });
  const timer = window.setTimeout(run, Math.max(0, fallbackMs));

  return () => {
    if (done) {
      return;
    }
    done = true;
    window.removeEventListener('pointerdown', run, true);
    window.removeEventListener('keydown', run, true);
    window.removeEventListener('touchstart', run, true);
    window.clearTimeout(timer);
  };
}

/** Skip LQIP only when progressive loading is disabled. Priority images still go low → sharp. */
export function shouldSkipProgressivePreview(progressive: boolean, _priority = false): boolean {
  return !progressive;
}
